import { Field, Form, Formik } from 'formik'
import _ from 'lodash'
import { Fragment, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Lottie from 'react-lottie-player'
import * as Yup from 'yup'
import { X } from 'lucide-react'
import buttonLoader from '../../animations/button_loading.json'
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet'
import { toast } from '@/hooks/use-toast'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import PrimaryButton from '../../components/PrimaryButton.jsx'
import { AuthContext } from '../../contexts/AuthContext.js'
import { MENTOR, STUDENT_PLUS } from '../../enums/userRoles.js'
import useIssueReporter from '../../hooks/useIssueReporter/useIssueReporter.js'
import useProctoring from '../../hooks/useProctoring/useProctoring.js'
import FileUploadTextareaInput from '../FileUploadTextareaInput.jsx'
import FormikSelectInput from '../FormikSelectInput.jsx'
import issueOptions from './_enums.js'
import { submitTicket } from './_services.js'
import { isSupportV3Enabled, retrieveSquadNo, retrieveSquadNoArray } from './_util.js'

const IssueReporter = ({ forceShow = false } = {}) => {
    const { isReporterOpen, onReporterOpen, onReporterClose, squad } =
        useIssueReporter()
    const { kalviumKc } = useContext(AuthContext)
    const location = useLocation()
    const isAssessmentRoute = location.pathname.startsWith('/assessment')
    const isAttemptReportRoute = location.pathname.includes('/report')
    const isVisibleOnRoute = forceShow || isAttemptReportRoute || !isAssessmentRoute

    const [description, setDescription] = useState('')
    const [uploadedFiles, setUploadedFiles] = useState([])
    const [isDescriptionEmpty, setIsDescriptionEmpty] = useState(true)
    const [isIssueTypeEmpty, setIsIssueTypeEmpty] = useState(true)
    const isMobile = useMediaQuery('(max-width: 768px)')
    const [studentDetails, setStudentDetails] = useState()
    const { getBrowserName, getBrowserVersion, getOS, getOSVersion } =
        useProctoring()

    const handleDescriptionChange = event => {
        const value = event.target.value
        setDescription(value)
        setIsDescriptionEmpty(value.length === 0)
    }

    const [isSubmitting, setIsSubmitting] = useState(false)

    const validateIssueType = value => {
        setIsIssueTypeEmpty(!value)
        return !value ? 'Required' : undefined
    }

    const validationSchema = Yup.object({
        issueType: Yup.string().required('This field is required'),
    })

    const showToast = ({ toastText, status = 'error' }) => {
        toast({
            title: toastText,
            variant: status === 'success' ? 'default' : 'destructive',
        })
    }

    const _handleSubmitTicket = async values => {
        try {
            setIsSubmitting(true)

            const ticketData = _getTicketMetadata(values)
            const result = await submitTicket(ticketData, kalviumKc.getToken())

            if (result.success) {
                showToast({
                    toastText: `We received your request. Please check your mail for further updates on this issue. Thank you!`,
                    status: 'success',
                })
                onReporterClose()
            } else {
                showToast({
                    toastText: `Failed to submit your request. Please try again later.`,
                })
            }
        } catch (error) {
            console.error('Error submitting ticket:', error)
            showToast({
                toastText: `An error occurred while processing your request. Please try again.`,
            })
        } finally {
            setDescription('')
            setIsSubmitting(false)
        }
    }

    const _getTicketMetadata = values => {
        const browserDetails = `Browser: ${getBrowserName()} ${getBrowserVersion()}`
        const osDetails = `OS: ${getOS()} ${getOSVersion()}`
        const windowLocationUrl = `URL: ${window.location.href}`
        const timestamp = `Time Stamp: ${new Date().toLocaleString()}`
        const userSquads = _.get(kalviumKc.getParsedToken(), 'groups', [])
        const squad = retrieveSquadNo(userSquads)
        const squads = retrieveSquadNoArray(userSquads) ?? null

        const metadataForStoringTicketInDb = {
            browser: `${getBrowserName()} ${getBrowserVersion()}`,
            os: `${getOS()} ${getOSVersion()}`,
            url: window.location.href,
            description: description,
            user_info: {
                id: _.get(kalviumKc.getParsedToken(), 'sub'),
                name: _.get(kalviumKc.getParsedToken(), 'name'),
                email: _.get(kalviumKc.getParsedToken(), 'email'),
                roles: _.get(kalviumKc.getParsedToken(), 'realm_access.roles'),
                squads: squads,
            },
        }

        return {
            description: `${description}\n\n${browserDetails}\n${osDetails}\n${windowLocationUrl}\n${timestamp}`,
            uploaded_files: uploadedFiles,
            email: _.get(kalviumKc.getParsedToken(), 'email'),
            name: _.get(kalviumKc.getParsedToken(), 'name'),
            squad,
            metadata: metadataForStoringTicketInDb,
            topic_id: values.issueType,
            issue_type: _.find(
                issueOptions,
                option => option.value === values.issueType
            ).label,
        }
    }

    useEffect(() => {
        if(!squad){
            const fetchStudentDetails = async () => {
                const result =  await fetch(
                    `${import.meta.env.VITE_STUDENT_API_ENDPOINT}/profile/${
                        kalviumKc?.getParsedToken()?.sub
                    }`,
                    {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${kalviumKc?.getToken()}`,
                            'Content-type': 'application/json',
                        },
                    }
                )  
                const response = await result?.json()
                const studentDetails =
                Array.isArray(response) && response.length > 0 && response[0]
                setStudentDetails(studentDetails)
            }
          fetchStudentDetails()
           
        }
    },[squad])

    const studentAppUrl = import.meta.env.VITE_STUDENT_NEXT_APP_BASE_URL
    const isKalviumSupportV3Enabled = isSupportV3Enabled(squad ?? studentDetails?.squadNumber)
    return (
        kalviumKc.hasRole([STUDENT_PLUS, MENTOR]) && isVisibleOnRoute && (
            <Fragment>
                {isKalviumSupportV3Enabled ? (
                    <a
                        href={studentAppUrl + 'support'}
                        className="no-underline"
                        target='_blank'
                        rel="noopener noreferrer"
                    >
                        <button
                            type="button"
                            className={cn(
                                'fixed right-0 bottom-[112px] bg-[#f2f2f2] dark:bg-gray-800',
                                'border-l-4 border-l-[#cb3434] border-t border-t-[#e3e3e3] dark:border-t-gray-700',
                                'border-b border-b-[#e3e3e3] dark:border-b-gray-700',
                                'p-2 md:p-4 opacity-70 md:opacity-100 z-[1399]',
                                'transition-colors flex items-center justify-center',
                                'h-auto w-auto'
                            )}
                        >
                            <img
                                src="/images/issueReporterIcon.svg"
                                alt="Kavlium-Support-Icon"
                                className="w-auto h-auto max-w-[32px] max-h-[32px]"
                            />
                        </button>
                    </a>
                ) : (
                    <button
                        type="button"
                        onClick={onReporterOpen}
                        className={cn(
                            'fixed right-0 bottom-[112px] bg-[#f2f2f2] dark:bg-gray-800',
                            'border-l-4 border-l-[#cb3434] border-t border-t-[#e3e3e3] dark:border-t-gray-700',
                            'border-b border-b-[#e3e3e3] dark:border-b-gray-700',
                            'p-2 md:p-4 opacity-70 md:opacity-100 z-[1399]',
                            'transition-colors flex items-center justify-center',
                            'h-auto w-auto'
                        )}
                    >
                        <img
                            src="/images/issueReporterIcon.svg"
                            alt="Kavlium-Support-Icon"
                            className="w-auto h-auto max-w-[32px] max-h-[32px]"
                        />
                    </button>
                )}
                <Sheet open={isReporterOpen} onOpenChange={onReporterClose}>
                    <SheetContent
                        side="right"
                        overlayClassName="!z-[100000]"
                        className={cn(
                            'w-full sm:max-w-lg md:max-w-[800px] border-l-4 border-l-[#333333] dark:border-l-gray-700',
                            'bg-white dark:bg-gray-900 transition-colors',
                            'overflow-y-auto [&::-webkit-scrollbar]:hidden',
                            '!z-[100001]' // Ensure it's above the banner (z-index 100000)
                        )}
                        style={{ zIndex: 100001 }}
                    >
                        <SheetHeader className="relative p-0 bg-[#fce9cc] dark:bg-gray-800 border-b border-b-[#333333] dark:border-b-gray-700 transition-colors">
                            <div className="absolute left-6 md:left-12 top-[98px] md:ml-4">
                                <SheetTitle className="font-rubik font-bold text-2xl text-[#454545] dark:text-gray-100 transition-colors">
                                    Kalvium Support
                                </SheetTitle>
                            </div>
                            <div className="flex justify-between">
                                <img
                                    src="/images/IssueReporterCircle.svg"
                                    alt="Logo"
                                />
                                <img
                                    src="/images/issueReporterWarningBoard.svg"
                                    alt="Logo"
                                />
                            </div>
                        </SheetHeader>
                        <div
                            className={cn(
                                'flex flex-col justify-between',
                                'mx-0 md:mx-16 mt-12',
                                'p-0',
                                'dark:bg-gray-900 transition-colors',
                                '[&::-webkit-scrollbar]:hidden'
                            )}
                        >
                            <Formik
                                initialValues={{
                                    issueType: '',
                                    description: '',
                                }}
                                validationSchema={validationSchema}
                                onSubmit={_handleSubmitTicket}
                            >
                                <Form className="flex flex-col gap-6 w-full">
                                    <div className="flex flex-col gap-6 w-full">
                                        <Field
                                            name="issueType"
                                            component={FormikSelectInput}
                                            validate={validateIssueType}
                                            zIndex={2}
                                            options={issueOptions}
                                            label="Help Topic"
                                            labelColor={'tundora'}
                                            isRequired={true}
                                            starColor={'flushmahogany'}
                                            placeholder="Select a help topic"
                                            width={isMobile ? 'full' : 96}
                                            emptySearchMessage="No matches found"
                                            menuListProps={{
                                                height: 52,
                                            }}
                                            sx={{
                                                '&::-webkit-scrollbar': {
                                                    width: 1,
                                                    bg: 'seashell',
                                                    position: 'absolute',
                                                    right: '10px',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    width: 0.5,
                                                    bg: 'alto3',
                                                },
                                            }}
                                        />

                                        <FileUploadTextareaInput
                                            label="Tell us more"
                                            name="description"
                                            placeholder="Explain in detail"
                                            variant="retro"
                                            size="md"
                                            allowFileUpload={true}
                                            maxFileSize={1}
                                            isRequired={true}
                                            value={description}
                                            onChange={handleDescriptionChange}
                                            onFilesChange={setUploadedFiles}
                                            maxNoOfFiles={5}
                                        />

                                        <PrimaryButton
                                            mt={6}
                                            mb={6}
                                            variant={'flat'}
                                            type={'submit'}
                                            color={'white'}
                                            backgroundColor={
                                                isSubmitting ? 'white' : 'rum'
                                            }
                                            isDisabled={
                                                isIssueTypeEmpty ||
                                                isDescriptionEmpty ||
                                                isSubmitting
                                            }
                                            sx={{
                                                _hover: {
                                                    boxShadow: 'none',
                                                },
                                                _active: {
                                                    boxShadow: 'none',
                                                },
                                            }}
                                        >
                                            {isSubmitting ? (
                                                <Lottie
                                                    loop
                                                    animationData={buttonLoader}
                                                    play={true}
                                                />
                                            ) : (
                                                'Submit'
                                            )}
                                        </PrimaryButton>
                                    </div>
                                </Form>
                            </Formik>
                        </div>
                    </SheetContent>
                </Sheet>
            </Fragment>
        )
    )
}

export default IssueReporter
